import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Wifi, ArrowDown, ArrowUp, Loader2, Plus } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PortalShell, Empty } from "../components/ProtectedRoute";
import { StatusTimeline, StatusBadge } from "../components/StatusTimeline";
import { TicketThread } from "../components/TicketThread";
import { PaymentMethods } from "../components/BuyDialog";
import { useAuth } from "../context/AuthContext";
import { useCountry } from "../context/CountryContext";
import { api, errMsg, money, fmtDate, TECH } from "../lib/api";

const MyWebsites = () => {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/sites/mine").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return null;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="my-websites-list">
      <Link to="/website-builder" className="grid min-h-[160px] place-items-center rounded-2xl border-2 border-dashed border-primary/40 bg-accent p-6 text-center font-bold text-primary" data-testid="new-website-card"><span><Plus className="mx-auto mb-2 h-6 w-6" />Create a free website with AI</span></Link>
      {rows.map((s) => (
        <Link key={s.id} to={`/website-builder/${s.id}`} className="overflow-hidden rounded-2xl border bg-white transition-transform hover:-translate-y-1" data-testid={`my-site-${s.slug}`}>
          <img src={s.photos?.[0]} alt="" className="h-28 w-full object-cover" />
          <div className="p-4"><p className="font-bold">{s.business_name}</p><p className="text-xs text-muted-foreground">/s/{s.slug} · {s.views} views</p><div className="mt-2"><StatusBadge status={s.status} /></div></div>
        </Link>))}
    </div>
  );
};

const TABS = [["services", "Services"], ["domains", "My Domains"], ["websites", "My Websites"], ["invoices", "Invoices"], ["support", "Support"]];

const ServiceCard = ({ s, onRequest }) => (
  <div className="rounded-lg border bg-white p-5" data-testid={`service-card-${s.id}`}>
    <div className="flex flex-wrap items-start gap-3">
      <Wifi className="h-5 w-5 text-primary" />
      <div className="flex-1">
        <p className="font-display text-lg font-bold">{s.name} <span className="text-sm font-medium text-muted-foreground">· {TECH[s.type]?.label}</span></p>
        <p className="mt-0.5 flex gap-3 font-mono-data text-xs text-muted-foreground">
          {s.type !== "hosting" && <><span className="flex items-center gap-1"><ArrowDown className="h-3 w-3" />{s.download_mbps} Mbps</span>
          <span className="flex items-center gap-1"><ArrowUp className="h-3 w-3" />{s.upload_mbps} Mbps</span></>}
          <span>{s.data_cap}</span><span>Order {s.order_number}</span>
        </p>
      </div>
      <div className="text-right">
        <p className="font-semibold">{money(s.price, s.currency)}<span className="text-xs text-muted-foreground">/mo</span></p>
        <p className="text-xs text-muted-foreground">{s.billing === "card_auto" ? "Auto-renews by card" : "Pay monthly via Pay now"}</p>
      </div>
    </div>
    <div className="mt-5"><StatusTimeline status={s.status} /></div>
    <div className="mt-4 flex flex-wrap items-center gap-2 border-t pt-4 text-xs text-muted-foreground">
      <span className="flex-1">{s.install_address ? `${s.install_address} · ` : ""}next billing {fmtDate(s.next_billing_date)}</span>
      {s.cancel_requested && <span className="font-semibold text-red-600">Cancellation requested</span>}
      <Button size="sm" variant="outline" onClick={() => onRequest(s, "upgrade")} data-testid={`service-upgrade-btn-${s.id}`}>Upgrade</Button>
      <Button size="sm" variant="ghost" onClick={() => onRequest(s, "cancel")} data-testid={`service-cancel-btn-${s.id}`}>Cancel</Button>
    </div>
  </div>
);

const RequestDialog = ({ req, onClose, onDone }) => {
  const [details, setDetails] = useState("");
  if (!req) return null;
  const submit = async () => {
    try {
      await api.post(`/me/services/${req.s.id}/request`, { type: req.type, details });
      toast.success("Request sent. Our team will reply in Support.");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="service-request-dialog">
        <DialogHeader><DialogTitle>{req.type === "upgrade" ? "Request an upgrade" : "Request cancellation"} · {req.s.name}</DialogTitle></DialogHeader>
        <Textarea value={details} onChange={(e) => setDetails(e.target.value)} placeholder={req.type === "upgrade" ? "Which speed would you like?" : "Tell us why you're leaving (optional)"} data-testid="service-request-details" />
        <Button onClick={submit} data-testid="service-request-submit">Send request</Button>
      </DialogContent>
    </Dialog>
  );
};

const PayDialog = ({ inv, onClose }) => {
  const { countries } = useCountry();
  const country = countries.find((c) => c.code === inv?.country);
  const [method, setMethod] = useState("mobile_money");
  const [busy, setBusy] = useState(false);
  if (!inv || !country) return null;
  const m = country.payment_methods.some((x) => x.id === method) ? method : "card";
  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/me/invoices/${inv.id}/pay`, { payment_method: m, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="invoice-pay-dialog">
        <DialogHeader><DialogTitle>Pay {inv.number} · {money(inv.amount, inv.currency)}</DialogTitle></DialogHeader>
        <PaymentMethods country={country} value={m} onChange={setMethod} />
        <Button onClick={pay} disabled={busy} data-testid="invoice-pay-confirm-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue to payment"}</Button>
      </DialogContent>
    </Dialog>
  );
};

const NewTicket = ({ onCreated }) => {
  const [f, setF] = useState({ subject: "", body: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/me/tickets", { ...f, category: "general" });
      setF({ subject: "", body: "" });
      onCreated(data);
      toast.success("Ticket opened");
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border bg-white p-5" data-testid="new-ticket-form">
      <p className="font-semibold">Open a support ticket</p>
      <div><Label>Subject</Label><Input className="mt-1" required value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="ticket-subject-input" /></div>
      <div><Label>Message</Label><Textarea className="mt-1" required value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="ticket-body-input" /></div>
      <Button data-testid="create-support-ticket-btn"><Plus className="mr-1 h-4 w-4" />Open ticket</Button>
    </form>
  );
};

export default function CustomerPortal() {
  const { user } = useAuth();
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") || "services";
  const [d, setD] = useState(null);
  const [req, setReq] = useState(null);
  const [payInv, setPayInv] = useState(null);
  const load = useCallback(() => api.get("/me/overview").then((r) => setD(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const toggleRenew = async (dom) => {
    await api.patch(`/me/domains/${dom.id}`, { auto_renew: !dom.auto_renew });
    toast.success(`Auto-renew ${!dom.auto_renew ? "on" : "off"} for ${dom.domain}`);
    load();
  };

  return (
    <PortalShell testid="customer-portal" title={`Hi ${user?.name?.split(" ")[0] || "there"}`} subtitle="Your services, domains, billing and support."
      tabs={TABS} active={tab} onTab={(t) => setSp({ tab: t })}>
      {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
        <>
          {tab === "services" && (
            <div className="space-y-4" data-testid="active-services-list">
              {d.services.length ? d.services.map((s) => <ServiceCard key={s.id} s={s} onRequest={(s, type) => setReq({ s, type })} />)
                : <Empty>No services yet. <Link to="/internet/fibre" className="font-semibold text-primary">Find a package</Link></Empty>}
              {d.orders.filter((o) => o.status !== "paid").map((o) => (
                <div key={o.id} className="flex items-center gap-3 rounded-lg border bg-white p-4 text-sm" data-testid={`pending-order-${o.id}`}>
                  <span className="flex-1">Order {o.number} · {money(o.total, o.currency)}</span><StatusBadge status={o.status} />
                </div>
              ))}
            </div>
          )}
          {tab === "domains" && (d.domains.length ? (
            <div className="overflow-x-auto rounded-lg border bg-white">
              <Table data-testid="customer-domains-table">
                <TableHeader><TableRow><TableHead>Domain</TableHead><TableHead>Status</TableHead><TableHead>Registrar</TableHead><TableHead>Expires</TableHead><TableHead>Auto-renew</TableHead></TableRow></TableHeader>
                <TableBody>{d.domains.map((x) => (
                  <TableRow key={x.id} data-testid={`domain-row-${x.domain}`}>
                    <TableCell className="font-mono-data">{x.domain} {x.free && <span className="ml-1 rounded bg-emerald-50 px-1.5 text-[10px] font-semibold text-emerald-700">FREE YR 1</span>}</TableCell>
                    <TableCell><StatusBadge status={x.status} /></TableCell>
                    <TableCell className="text-xs">{x.registrar}{x.registrar_mode === "simulated" && " (simulated)"}</TableCell>
                    <TableCell>{fmtDate(x.expires_at)}</TableCell>
                    <TableCell><Switch checked={x.auto_renew} onCheckedChange={() => toggleRenew(x)} data-testid={`domain-autorenew-${x.domain}`} /></TableCell>
                  </TableRow>))}
                </TableBody>
              </Table>
            </div>) : <Empty>No domains yet. <Link to="/domains" className="font-semibold text-primary">Search domains</Link></Empty>)}
          {tab === "websites" && <MyWebsites />}
          {tab === "invoices" && (d.invoices.length ? (
            <div className="overflow-x-auto rounded-lg border bg-white">
              <Table data-testid="customer-invoices-list">
                <TableHeader><TableRow><TableHead>Invoice</TableHead><TableHead>Date</TableHead><TableHead>Items</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead /></TableRow></TableHeader>
                <TableBody>{d.invoices.map((i) => (
                  <TableRow key={i.id} data-testid={`invoice-row-${i.number}`}>
                    <TableCell className="font-mono-data text-xs">{i.number}</TableCell>
                    <TableCell>{fmtDate(i.created_at)}</TableCell>
                    <TableCell className="max-w-xs text-xs text-muted-foreground">{i.items.map((x) => x.name).join(", ")}</TableCell>
                    <TableCell className="font-semibold">{money(i.amount, i.currency)}</TableCell>
                    <TableCell><StatusBadge status={i.status} /></TableCell>
                    <TableCell>{i.status === "unpaid" && <Button size="sm" onClick={() => setPayInv(i)} data-testid={`invoice-pay-btn-${i.number}`}>Pay now</Button>}</TableCell>
                  </TableRow>))}
                </TableBody>
              </Table>
            </div>) : <Empty>No invoices yet.</Empty>)}
          {tab === "support" && (
            <div className="grid gap-6 lg:grid-cols-3">
              <div className="lg:col-span-1"><NewTicket onCreated={load} /></div>
              <div className="space-y-4 lg:col-span-2" data-testid="tickets-list">
                {d.tickets.length ? d.tickets.map((t) => <TicketThread key={t.id} ticket={t} onUpdate={load} />) : <Empty>No tickets yet.</Empty>}
              </div>
            </div>
          )}
        </>
      )}
      <RequestDialog req={req} onClose={() => setReq(null)} onDone={() => { setReq(null); load(); }} />
      <PayDialog inv={payInv} onClose={() => setPayInv(null)} />
    </PortalShell>
  );
}
